import React, { useState, useEffect } from "react";
import { GoogleOAuthProvider, useGoogleLogin } from "@react-oauth/google";
import axios from "axios";
import CryptoJS from "crypto-js";
import {
  Alert,
  Box,
  Button,
  Container,
  Stack,
  styled,
  Tooltip,
  Typography,
} from "@mui/material";
import { Google, Logout } from "@mui/icons-material";
import PropertiesSearch from "./PropertiesSearch";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ContentPasteGoIcon from "@mui/icons-material/ContentPasteGo";
import Papa from "papaparse";

// ----------------------------------------------------------------------

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

// ----------------------------------------------------------------------

const ContentStyle = styled("div")(({ theme }) => ({
  maxWidth: 480,
  margin: "auto",
  display: "flex",
  height: "auto",
  flexDirection: "column",
  justifyContent: "center",
  padding: theme.spacing(12, 0),
}));

// ----------------------------------------------------------------------

const DropZoneStyle = styled("div")(({ theme }) => ({
  outline: "none",
  display: "flex",
  overflow: "hidden",
  textAlign: "center",
  position: "relative",
  alignItems: "center",
  flexDirection: "column",
  justifyContent: "center",
  padding: theme.spacing(2, 0),
  borderRadius: theme.shape.borderRadius,
  transition: theme.transitions.create("padding"),
  backgroundColor: theme.palette.background.default,
  border: `1px dashed ${theme.palette.grey[500]}`,
  "&:hover": {
    opacity: 0.72,
    cursor: "pointer",
  },
  [theme.breakpoints.up("md")]: { textAlign: "left", flexDirection: "row" },
}));

// ----------------------------------------------------------------------

const ImageSearcher = () => {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [data, setData] = useState<any[]>([]);
  const [materialType, setMaterialType] = useState<string[]>([]);
  const [productType, setProductType] = useState<string[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedProductCode, setSelectedProductCode] = useState<string>("");
  const [folderPathMap, setFolderPathMap] = useState({});

  const login = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      setAccessToken(tokenResponse.access_token);
      const expiry = Date.now() + tokenResponse.expires_in * 1000;
      localStorage.setItem("google_access_token", tokenResponse.access_token);
      localStorage.setItem("google_token_expiry", expiry.toString());
      setError(null);
    },
    onError: (errorResponse) =>
      setError(
        "Lỗi đăng nhập: " +
          (errorResponse.error_description || "Không xác định")
      ),
    scope: "https://www.googleapis.com/auth/drive.readonly",
  });
  const METADATA_CSV_URL =
    "https://docs.google.com/spreadsheets/d/e/2PACX-1vRCtROGlsgz3-UAtt-eJfKCN1KarEcWBWtDrMRfJuJ_Lbz6Y9-R5SA0CuHbV--KepvBo5Had9bLUb4p/pub?gid=522274208&single=true&output=csv";

  const handleSignOut = () => {
    localStorage.removeItem("google_access_token");
    localStorage.removeItem("google_token_expiry");
    setAccessToken(null);
    setError(null);
  };

  const handleFileUpload = (e: any) => {
    if (!e.target.files || e.target.files.length === 0) {
      setError("Vui lòng chọn file hình ảnh.");
      return;
    }
    const file = e.target.files[0];
    if (file && file.type.startsWith("image/")) {
      setUploadedFile(file);
      setResult("");
      setError(null);
    } else {
      setError("Vui lòng upload file hình ảnh.");
    }
  };

  const computeHash = async (file: any) => {
    const arrayBuffer = await file.arrayBuffer();
    const wordArray = CryptoJS.lib.WordArray.create(arrayBuffer);
    return CryptoJS.SHA256(wordArray).toString(CryptoJS.enc.Hex);
  };

  const searchImageInDrive = async (
    targetHash: string
  ): Promise<string | null> => {
    try {
      const response = await axios.get(METADATA_CSV_URL);
      const metadata = await new Promise((resolve, reject) => {
        Papa.parse(response.data, {
          header: true,
          complete: (results) => resolve(results.data),
          error: (err) => reject(err),
        });
      });

      const match = (metadata as any[]).find(
        (item) => item["Hash"] === targetHash
      );
      return match ? match["Folder Path"] : null;
    } catch (err) {
      setError(
        "Lỗi khi đọc metadata: " +
          (err instanceof Error ? err.message : "Không xác định")
      );
      return null;
    }
  };

  const handleSearch = async () => {
    if (!accessToken) {
      setError("Vui lòng đăng nhập Google trước.");
      return;
    }
    if (!uploadedFile) {
      setError("Vui lòng upload hình trước.");
      return;
    }
    setLoading(true);
    setError(null);
    setResult("");

    try {
      const targetHash = await computeHash(uploadedFile);
      const foundFolder = await searchImageInDrive(targetHash);
      if (foundFolder) {
        // setResult(`Hình nằm trong folder: ${foundFolder}`);
        const folderParts = foundFolder.trim().split("/");
        const code = folderParts[folderParts.length - 2]; // Lấy tên folder cuối (trước dấu / cuối)
        setSelectedProductCode(code);
      } else {
        setResult("Không tìm thấy hình phù hợp trong Drive.");
      }
    } catch (err) {
      setError(
        "Lỗi khi tìm kiếm: " +
          (err instanceof Error ? err.message : "Không xác định")
      );
    } finally {
      setLoading(false);
    }
  };

  function truncateFileName(name: string, maxLength = 20) {
    if (name.length <= maxLength) return name;
    const start = name.slice(0, 8);
    const end = name.slice(-8);
    return `${start}...${end}`;
  }

  const handlePasteFromButton = async () => {
    try {
      const permission = await navigator.permissions.query({
        name: "clipboard-read" as PermissionName,
      });
      if (permission.state === "denied") {
        setError(
          "Quyền truy cập clipboard bị từ chối. Vui lòng sử dụng Ctrl+V để dán hình ảnh."
        );
        return;
      }

      const clipboardItems = await navigator.clipboard.read();
      for (const item of clipboardItems) {
        for (const type of item.types) {
          if (type.startsWith("image/")) {
            const blob = await item.getType(type);
            const file = new File(
              [blob],
              `pasted_image_${Date.now()}.${type.split("/")[1]}`,
              { type }
            );
            setUploadedFile(file);
            setResult("");
            setError(null);
            return;
          }
        }
      }
      setError("Không tìm thấy hình ảnh trong clipboard.");
    } catch (err) {
      console.error("Lỗi khi đọc clipboard:", err);
      setError(
        "Lỗi khi đọc clipboard. Vui lòng đảm bảo trình duyệt hỗ trợ và sử dụng HTTPS."
      );
    }
  };

  const fetchData = async () => {
    setDataLoading(true);
    const csvUrl =
      "https://docs.google.com/spreadsheets/d/e/2PACX-1vRCtROGlsgz3-UAtt-eJfKCN1KarEcWBWtDrMRfJuJ_Lbz6Y9-R5SA0CuHbV--KepvBo5Had9bLUb4p/pub?gid=201629599&single=true&output=csv";
    try {
      const response = await axios.get(csvUrl);
      Papa.parse(response.data, {
        header: true,
        complete: (results) => {
          const parsedData = results.data.filter(
            (item: any) => item["Code"] && item["Code"].trim() !== ""
          );
          setData(parsedData);

          const materialSet = new Set<string>();
          const productSet = new Set<string>();
          parsedData.forEach((item: any) => {
            if (item["Wood type"]) materialSet.add(item["Wood type"]);
            if (item["Category"]) {
              if (item["Category"].includes(",")) {
                item["Category"]
                  .split(",")
                  .forEach((cat: string) => productSet.add(cat.trim()));
              } else {
                productSet.add(item["Category"]);
              }
            }
          });

          setMaterialType(Array.from(materialSet).sort());
          setProductType(Array.from(productSet).sort());
          setDataLoading(false);
        },
        error: (err) => {
          setError((err as Error).message);
          setDataLoading(false);
        },
      });
    } catch (err) {
      setError((err as Error).message);
      setDataLoading(false);
    }
  };

  useEffect(() => {
    const handlePaste = async (event: Event) => {
      const clipboardEvent = event as ClipboardEvent;
      const items = clipboardEvent.clipboardData?.items;
      if (!items) return;

      for (const item of Array.from(items)) {
        if (item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            setUploadedFile(file);
            setResult("");
            setError(null);
          } else {
            setError("Không thể lấy hình ảnh từ clipboard.");
          }
          break;
        }
      }
    };

    window.addEventListener("paste", handlePaste);
    return () => {
      window.removeEventListener("paste", handlePaste);
    };
  }, []);

  useEffect(() => {
    fetchData();
    const storedToken = localStorage.getItem("google_access_token");
    const storedExpiry = localStorage.getItem("google_token_expiry");
    if (storedToken && storedExpiry) {
      const expiry = parseInt(storedExpiry, 10);
      if (Date.now() < expiry) {
        setAccessToken(storedToken);
      } else {
        localStorage.removeItem("google_access_token");
        localStorage.removeItem("google_token_expiry");
      }
    }
  }, []);

  useEffect(() => {
    if (accessToken) {
      const interceptor = axios.interceptors.response.use(
        (response) => response,
        (err) => {
          if (err.response && err.response.status === 401) {
            localStorage.removeItem("google_access_token");
            localStorage.removeItem("google_token_expiry");
            setAccessToken(null);
            setError("Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.");
          }
          return Promise.reject(err);
        }
      );
      return () => {
        axios.interceptors.response.eject(interceptor);
      };
    }
  }, [accessToken]);

  return (
    <Container maxWidth="sm">
      {!accessToken ? (
        <ContentStyle className="">
          <Typography variant="h5" gutterBottom>
            HOÀNG HUY TOOL
          </Typography>
          <Stack direction="row" alignItems="center" sx={{ mb: 5 }}>
            <Box sx={{ flexGrow: 1 }}>
              <Typography sx={{ color: "text.secondary" }}>
                Đăng nhập bằng tài khoản Google
              </Typography>
            </Box>
            <Tooltip title="Login">
              <Box
                component="img"
                src={`/images/illustration_login.png`}
                sx={{ width: 32, height: 32 }}
              />
            </Tooltip>
          </Stack>
          <Alert severity="info" sx={{ mb: 3 }}>
            Liên hệ admin để lấy tài khoản
            <strong>&nbsp;tài khoản</strong>
          </Alert>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Google />}
            onClick={() => login()}
            sx={{ textTransform: "none" }}
          >
            Đăng nhập Google
          </Button>
        </ContentStyle>
      ) : (
        <Stack
          direction="row"
          alignItems="center"
          sx={{ mb: 5 }}
          className="w-full"
        >
          <Box className="w-full">
            <Box className="flex flex-row items-center justify-between">
              <Typography variant="h6">HOÀNG HUY TOOL</Typography>
              <Logout color="error" className="my-3" onClick={handleSignOut} />
            </Box>
            {/* <br />
            <DropZoneStyle>
              <ContentPasteGoIcon
                color="primary"
                className="absolute top-1 left-1"
                onClick={handlePasteFromButton}
              />
              <Button
                component="label"
                role={undefined}
                variant="contained"
                tabIndex={-1}
                startIcon={<CloudUploadIcon />}
              >
                {truncateFileName(uploadedFile?.name || "Upload Image")}
                <VisuallyHiddenInput
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                />
              </Button>
            </DropZoneStyle>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSearch}
              disabled={loading || !accessToken}
              sx={{ my: 1, width: "100%" }}
            >
              Tìm kiếm hình ảnh
            </Button> */}
            {loading && (
              <Typography sx={{ m: 1 }}>Đang tìm kiếm hình ảnh...</Typography>
            )}
            {dataLoading ? (
              <Typography>Đang tải dữ liệu...</Typography>
            ) : error ? (
              <Typography color="error">Lỗi: {error}</Typography>
            ) : data?.length > 0 &&
              materialType.length > 0 &&
              productType.length > 0 ? (
              <PropertiesSearch
                data={data}
                materialType={materialType}
                productType={productType}
                onSearch={setFilteredData}
                accessToken={accessToken}
                initialProductCode={selectedProductCode}
              />
            ) : (
              <Typography>Không có dữ liệu để hiển thị.</Typography>
            )}

            {error && (
              <Typography sx={{ m: 1, color: "red" }}>{error}</Typography>
            )}
            {result && <Typography sx={{ m: 1 }}>{result}</Typography>}
          </Box>
        </Stack>
      )}
    </Container>
  );
};

function ImageDriveSearcher() {
  return (
    <GoogleOAuthProvider
      clientId={process.env.REACT_APP_GG_DRIVE_CLIENT_ID || ""}
    >
      <ImageSearcher />
    </GoogleOAuthProvider>
  );
}

export default ImageDriveSearcher;
